import React, { useState, useEffect } from 'react';
import { Trash2, CheckCircle, Clock, Phone, Mail, User, Calendar, MessageSquare, Eye, Car, Globe, Users } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const AdminInquiries = () => {
  const [carInquiries, setCarInquiries] = useState([]);
  const [tourInquiries, setTourInquiries] = useState([]);
  const [activeTab, setActiveTab] = useState('car');
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    const token = localStorage.getItem('adminToken');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      const [carRes, tourRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/inquiries/car`, config),
        axios.get(`${API_BASE_URL}/api/admin/inquiries/tour`, config)
      ]);
      setCarInquiries(carRes.data);
      setTourInquiries(tourRes.data);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (type, id, status) => {
    const token = localStorage.getItem('adminToken');
    try {
      await axios.put(`${API_BASE_URL}/api/admin/inquiries/${type}/${id}`, 
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchInquiries();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const deleteInquiry = async (type, id) => {
    if (window.confirm('Are you sure you want to delete this inquiry?')) {
      const token = localStorage.getItem('adminToken');
      try {
        await axios.delete(`${API_BASE_URL}/api/admin/inquiries/${type}/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchInquiries();
      } catch (error) {
        console.error('Error deleting inquiry:', error);
      }
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      contacted: 'bg-blue-500/20 text-blue-400',
      completed: 'bg-green-500/20 text-green-400'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };

  const getStatusIcon = (status) => {
    if (status === 'pending') return <Clock size={14} className="text-yellow-400" />;
    if (status === 'contacted') return <Eye size={14} className="text-blue-400" />;
    return <CheckCircle size={14} className="text-green-400" />;
  };

  const inquiries = activeTab === 'car' ? carInquiries : tourInquiries;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Inquiries</h1>

      {/* Tabs */}
      <div className="flex space-x-2 mb-6 border-b border-dark-700">
        <button
          onClick={() => setActiveTab('car')}
          className={`px-4 py-2 font-medium transition ${
            activeTab === 'car'
              ? 'text-orange-500 border-b-2 border-orange-500'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          <div className="flex items-center space-x-2">
            <Car size={16} />
            <span>Car Rentals ({carInquiries.length})</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('tour')}
          className={`px-4 py-2 font-medium transition ${
            activeTab === 'tour'
              ? 'text-orange-500 border-b-2 border-orange-500'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          <div className="flex items-center space-x-2">
            <Globe size={16} />
            <span>Tour Bookings ({tourInquiries.length})</span>
          </div>
        </button>
      </div>

      {/* Inquiries List */}
      <div className="space-y-4">
        {inquiries.length === 0 ? (
          <div className="bg-dark-900 rounded-lg p-8 text-center text-gray-500 border border-dark-700">
            No inquiries found
          </div>
        ) : (
          inquiries.map((inquiry) => (
            <div key={inquiry.id} className="bg-dark-900 rounded-lg border border-dark-700 hover:border-orange-500/30 transition-all duration-200">
              <div className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-4 mb-3">
                      <div className="flex items-center space-x-2">
                        <div className="bg-orange-500/20 p-1.5 rounded-full">
                          <User size={14} className="text-orange-500" />
                        </div>
                        <span className="font-medium text-white">{inquiry.name}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="bg-blue-500/20 p-1.5 rounded-full">
                          <Mail size={14} className="text-blue-400" />
                        </div>
                        <a href={`mailto:${inquiry.email}`} className="text-blue-400 hover:underline text-sm">
                          {inquiry.email}
                        </a>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="bg-green-500/20 p-1.5 rounded-full">
                          <Phone size={14} className="text-green-400" />
                        </div>
                        <a href={`tel:${inquiry.phone}`} className="text-gray-400 text-sm hover:text-white">
                          {inquiry.phone}
                        </a>
                      </div>
                    </div>

                    {activeTab === 'car' ? (
                      <>
                        <div className="mb-2">
                          <span className="text-gray-400 text-sm">Car:</span>
                          <span className="text-white text-sm ml-2 font-medium">{inquiry.car_name}</span>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-400 mb-2">
                          <div className="flex items-center space-x-1">
                            <Calendar size={14} className="text-orange-500" />
                            <span>Start: {new Date(inquiry.start_date).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Calendar size={14} className="text-orange-500" />
                            <span>End: {new Date(inquiry.end_date).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="mb-2">
                          <span className="text-gray-400 text-sm">Tour:</span>
                          <span className="text-white text-sm ml-2 font-medium">{inquiry.tour_name}</span>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-400 mb-2">
                          <div className="flex items-center space-x-1">
                            <Users size={14} className="text-orange-500" />
                            <span>People: {inquiry.number_of_people}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Calendar size={14} className="text-orange-500" />
                            <span>Preferred: {new Date(inquiry.preferred_date).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </>
                    )}

                    {inquiry.message && (
                      <div className="bg-dark-800 rounded-lg p-3 mt-2 border border-dark-700">
                        <div className="flex items-center space-x-2 mb-2">
                          <MessageSquare size={14} className="text-orange-500" />
                          <span className="text-sm font-medium text-gray-300">Message:</span>
                        </div>
                        <p className="text-gray-400 text-sm leading-relaxed">{inquiry.message}</p>
                      </div>
                    )}

                    <div className="text-xs text-gray-500 mt-3">
                      Received: {new Date(inquiry.created_at).toLocaleString()}
                    </div>
                  </div>

                  <div className="flex flex-col items-end space-y-3 ml-4">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(inquiry.status)}
                      <select
                        value={inquiry.status}
                        onChange={(e) => updateStatus(activeTab, inquiry.id, e.target.value)}
                        className={`text-xs px-2 py-1 rounded-full border border-dark-600 bg-dark-800 ${getStatusBadge(inquiry.status)} focus:outline-none focus:border-orange-500 cursor-pointer`}
                      >
                        <option value="pending">Pending</option>
                        <option value="contacted">Contacted</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                    <button
                      onClick={() => deleteInquiry(activeTab, inquiry.id)}
                      className="text-red-400 hover:text-red-300 transition p-1"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminInquiries;